<template>
    <div id="page">
        <slot name="header">
            <header id="header">
                <b-row id="header-top" class="pt-3">
                    <img v-if="logo" id="header-logo" class="col-auto" :src="logo" alt="Logo" />
                    <div v-if="header" id="header-title" class="col align-items-center">{{header}}</div>
                </b-row>
                <div id="header-bottom"></div>
            </header>
        </slot>

        <slot name="navigation" />
        
        <b-container id="content">
            <slot />
        </b-container>

        <slot name="footer">
            <vue-render v-if="footer" :code="footer" />
        </slot>

        <bootstrap-helper />
    </div>
</template>

<script>
    import BootstrapHelper from '@/shared/components/BootstrapHelper.vue'

    export default {
        name: 'PageLayout',

        props: {
            header: String,
            logo: String,
            footer: String
        },

        components: {
            BootstrapHelper
        }
    }
</script>

<style lang="scss">
    /* Colors */

    $primary: #019670 !default;
    $secondary: #353535 !default;
    $success: #28a745 !default;
    $info: #17a2b8 !default;
    $warning: #ffc107 !default;
    $danger: #dc3545 !default;
    $light: #f8f9fa !default;
    $dark: #343a40 !default;

    $link-color: #019670;
    $hover-color: #005b43;
    $h-color: #000000;
    $text-color: #353535;
    $placeholder-color: #a8a7a7;

    // Include Bootstrap and BootstrapVue SCSS files
    @import "~bootstrap/scss/bootstrap.scss";
    @import "~bootstrap-vue/src/index.scss";

    /* Layout */

    #header {
        margin-bottom: -20px;
    }

    #header-top {
        height: 115px;
        display: flex;
        margin: 0;
    }

    #header-bottom {
        height: 60px;
        background-image: linear-gradient(to bottom, $hover-color, $primary);
    }

    #header-logo {
        height: 59px;
        padding: 0;
        margin-left: 1rem;
    }

    // MD Medium devices (tablets, 768px and up)
    @media (min-width: 768px) {
        #header-logo {
            margin-left: 95px;
        }
    }

    #header-title {
        font-family: "Roboto Regular";
        font-size: 28px;
        line-height: 59px;
        color: $primary;
        white-space: nowrap;
    }

    /* footer */

    @media (max-width: 768px) {
        .footer1-buttons {
            margin-left: -10px;
        }
    }

    #footer-1 {
        height: 243px;
        background-image: linear-gradient(to bottom, $hover-color, $primary);
        text-align: center;
        margin-top: -8px;
    }

    .footer1-title {
        margin-top: 32px; // 43px - 11px (subtract 11px for margin, paddig odgl) 
        font-family: "Roboto Bold";
        font-size: 24pt;
        color: $light;
        white-space: nowrap;
    }

    .footer1-buttons {
        margin-top: 21px; // 33px - 12xp
        white-space: nowrap;
    }

    @media screen and (min-width: 577px) {
        div.footer1-content {
            font-size: 19pt;
        }
    }

    @media screen and (max-width: 576px) {
        div.footer1-content {
            font-size: 14pt;
        }
    }

    .footer1-content {
        margin-top: 25px; // 33px - 8px
        font-family: "Roboto Bold";
        font-size: 19pt;
        color: $light;
        white-space: nowrap;
    }

    span.mandatory {
        color: black !important;
    }

    .hide-mandatory span.mandatory {
        display: none;
    }

    .btn-cta {
        color: $primary;
        font-family: Roboto Bold;
        font-size: 18pt;
        text-align: center;
        text-transform: uppercase;
        padding-right: 30px;
        padding-left: 30px;
        background: #fff;
        border: 3px solid;
        border-top-left-radius: 7px;
        border-bottom-left-radius: 0px;
        border-color: #009770;
        min-height: 55px;
    }

    .btn-cta-icon {
        font-family: Roboto Bold;
        font-size: 18pt;
        text-align: center;
        text-transform: uppercase;
        padding-right: 15px;
        padding-left: 15px;
        color: #fff;
        background: #009770;
        border: 3px solid;
        border-top-right-radius: 7px;
        border-bottom-right-radius: 7px;
        border-color: #009770;
        min-height: 55px;
    }

    #footer-2 {
        height: 128px;
        background-color: $h-color;
    }

    .footer2-links {
        margin-top: 35px;
        text-align: center;
        white-space: nowrap;
        color: $placeholder-color;
    }

    @media screen and (max-width: 767px) {
        .footer2-links {
            margin-left: -10px;
        }
    }

    .footer2-copyright {
        font-family: "Roboto Bold";
        margin-top: 35px;
        text-align: center;
        white-space: nowrap;
        font-size: 16px;
        color: $light;
    }

    #footer a:hover {
        color: #00ae8a;
        text-decoration: none;
    }

    @media screen and (min-width: 768px) {
        #footer a {
            font-family: "Roboto Bold";
            font-size: 16px;
            color: #7f7f7f;
            margin-left: 2px;
            margin-right: 2px;
        }
    }

    @media screen and (max-width: 767px) {
        #footer a {
            font-family: "Roboto Bold";
            font-size: 10px;
            color: #7f7f7f;
            margin-left: 2px;
            margin-right: 2px;
        }
    }

    /* content */

    #content {
        margin-top: 45px;
        margin-bottom: 30px;
    }

    hr {
        color: #d1d1d1;
        margin-top: 40px;
        margin-bottom: 55px;
    }

    /* texts */

    body {
        font-family: "Roboto Regular";
        font-size: 16px;
        color: $text-color;
    }

    h1 {
        font-family: "Roboto Bold";
        font-size: 24px;
        color: $h-color;
    }

    h2 {
        font-family: "Roboto Bold";
        font-size: 16px;
        color: $h-color;
    }

    a {
        font-family: "Roboto Regular";
        font-size: 16px;
        color: $link-color;
        text-decoration: underline;
    }

    /* Forms */

    label {
        min-height: 22px;
    }

    .btn {
        font-family: Roboto Bold;
        font-size: 16px;
        text-align: center;
        vertical-align: middle;
        text-transform: uppercase;
        height: 58px;
        min-width: 131px;
        padding-right: 20px;
        padding-left: 20px;
        border-radius: 7px;
        border: 0px;
    }

    .btn-sm {
        font-size: 14px;
        height: 32px;
    }

    .btn-lg {
        height: 58px;
        min-width: 292px;
    }

    .btn-primary {
        color: #fff;
        background-image: linear-gradient(to right, #87be25, #7bad56);
    }

    .btn-secondary {
        color: #353535;
        background-image: linear-gradient(to right, #dfdfdf, #cfcfcf);
    }

    /* Progress tabs */

    .progress-finished {
        color: #ffffff;
        background-color: #87be25;
    }

    .progress-active {
        color: #ffffff;
        background-color: #87be25;
    }

    .progress-upcoming {
        color: #959595;
        background-color: #cfcfcf;
    }

    .progress-steps {
        color: #353535;
        background-color: #cfcfcf;
    }

    /* Placeholders */

    .form-control::-webkit-input-placeholder,
    .form-control::placeholder {
        color: $placeholder-color;
    }

    .b-form-file.custom-error ~ .invalid-feedback a {
        color: $danger;
    }

    .custom-invalid-feedback {
        display: none;
        width: 100%;
        font-size: 80%;
        color:$danger;
    }

    .was-validated .custom-invalid-feedback {
        display: block;
    }

    .was-validated .custom-control-input:invalid ~ .custom-control-label, .custom-control-input.is-invalid ~ .custom-control-label {
        color: $text-color;
    }

    .was-validated .custom-control-input:valid ~ .custom-control-label, .custom-control-input.is-valid ~ .custom-control-label {
        color: $text-color;
    }

    .was-validated .red-errors .custom-control-input:invalid ~ .custom-control-label, .was-validated .red-errors .custom-control-input:invalid ~ .custom-control-label a {
        color: $danger;
    }

    .link-secondary {
        color: $secondary;
        text-decoration: underline;
    }

    .btn-small {
        height: inherit;
        min-width: inherit;
    }

    // SM Small devices (landscape phones, 576px and up)
    // MD Medium devices (tablets, 768px and up)
    // LG Large devices (desktops, 992px and up)
    // XL Extra large devices (large desktops, 1200px and up)

</style>
